﻿import React, {JSX, useCallback, useMemo, useState} from "react";
import MaterialIcon from "../MaterialIcon";
import {MvvmPropBase} from "./MvvmControlProps";

type RepeaterRowProps = {
	index: number;
	children: JSX.Element;
	onRemove: (index: number) => void;
	removeTitle?: string;
};

function RepeaterRow(props: RepeaterRowProps): JSX.Element {
	const {index, onRemove, removeTitle, children} = props;
	const onClick = useCallback(function (e: React.MouseEvent<HTMLButtonElement>): void {
		e.preventDefault();
		onRemove(index);
	}, [index, onRemove]);

	return (<div className="widget-repeater__row">
		<div className="widget-repeater__row__remove">
			<button type="button" onClick={onClick}>
				<MaterialIcon icon="delete" title={removeTitle ?? "Remove row"}/>
			</button>
		</div>
		<div className="widget-repeater__row__entry">
			{children}
		</div>
	</div>);
}

export type MvvmRepeaterProps<TModel extends object, TValue extends object> = MvvmPropBase<TModel> & {
	addLabel: string;
	removeTitle?: string;
	minItems?: number;
	maxItems?: number;
	children: (item: TValue) => JSX.Element;
	addFactory?: () => TValue;
}

export default function MvvmRepeater<TModel extends object, TValue extends object>(props: MvvmRepeaterProps<TModel, TValue>): JSX.Element {
	const {context, property, addLabel, removeTitle, minItems, maxItems, children} = props;

	const items = context.getValue<TValue[]>(property) ?? [];
	const minimumItems = minItems ?? 0;
	const maximumItems = maxItems ?? Number.MAX_SAFE_INTEGER;
	const [state, setState] = useState<TValue[]>(items);

	const removeItem = useCallback(function (index: number): void {
		const copy = items.toSpliced(index, 1)
		context.setValue(property, copy, true);
		setState(copy);
	}, []);

	const addItem = useCallback(function (): void {
		const copy = [...items, props.addFactory?.() ?? ({} as TValue)]
		context.setValue(property, copy, true);
		setState(copy);
	}, []);

	const klassName = useMemo(() => {
		return "widget-repeater__rows"
			+ (items.length <= minimumItems ? " widget-repeater__rows--min" : "")
			+ (items.length >= maximumItems ? " widget-repeater__rows--max" : "");
	}, [items.length, minimumItems, maximumItems]);

	return (
		<div className="widget-repeater">
			<div className={klassName}>
				{state.map((item, index) => {
					return <RepeaterRow key={index}
					                    index={index}
					                    onRemove={removeItem}
					                    removeTitle={removeTitle}
					                    children={children(item)}/>;
				})}
			</div>
			{items.length < maximumItems &&
                <div className="widget-repeater__row widget-repeater__row--add">
                    <button type="button" onClick={addItem}>
                        <MaterialIcon icon="add_circle"/>
                        <span>{addLabel}</span>
                    </button>
                </div>}
		</div>
	);
}
